/**
 * Utility functions for handling NestJS error responses
 * Error format is normalized by axios interceptor to: { error: { statusCode, message, name, details, timestamp, path } }
 */
import type { AxiosError } from 'axios';

/**
 * Extract error message from normalized error response
 * @param error - Axios error object (normalized by interceptor)
 * @returns Error message
 */
export function getErrorMessage(error: unknown): string {
  const axiosError = error as AxiosError;

  if (!axiosError?.response?.data) {
    return axiosError?.message || 'An unexpected error occurred';
  }

  const data = axiosError.response.data as {
    error?: { message?: string };
    message?: string;
  };

  return data.error?.message || data.message || 'An error occurred';
}

/**
 * Extract error code/name from normalized error response
 * @param error - Axios error object
 * @returns Error name/code
 */
export function getErrorCode(error: unknown): string | undefined {
  const axiosError = error as AxiosError;

  if (!axiosError?.response?.data) {
    return undefined;
  }

  const data = axiosError.response.data as {
    error?: { name?: string; code?: string };
    errorCode?: string;
  };

  return data.error?.name || data.errorCode || data.error?.code;
}

/**
 * Extract status code from normalized error response
 * @param error - Axios error object
 * @returns Status code
 */
export function getStatusCode(error: unknown): number | undefined {
  const axiosError = error as AxiosError;

  if (!axiosError?.response) {
    return undefined;
  }

  const data = axiosError.response.data as {
    error?: { statusCode?: number };
    statusCode?: number;
  };

  return axiosError.response.status || data.error?.statusCode || data.statusCode;
}

/**
 * Get full error information object
 * @param error - Axios error object
 * @returns Error info object
 */
export function getErrorInfo(error: unknown): {
  message: string;
  errorCode?: string;
  statusCode?: number;
  details?: unknown;
  timestamp?: string;
  path?: string;
} {
  const axiosError = error as AxiosError;
  const data = axiosError?.response?.data as {
    error?: {
      details?: unknown;
      timestamp?: string;
      path?: string;
    };
  };

  return {
    message: getErrorMessage(error),
    errorCode: getErrorCode(error),
    statusCode: getStatusCode(error),
    details: data?.error?.details || null,
    timestamp: data?.error?.timestamp,
    path: data?.error?.path,
  };
}
